// src/services/analytics.ts

interface User {
  id: string
  email: string
  role: string
  username?: string
  organization?: string
  createdAt?: string
  plan?: string
}

class AnalyticsService {
  private measurementId: string = process.env.VUE_APP_GA_MEASUREMENT_ID || 'G-W9G6Q0GYPF'
  private isEnabled: boolean = true
  private currentUser: any = null

  // CONSTRUCTOR - Load user from storage on init
  constructor() {
    this.loadUserFromStorage()
  }

  // Load user from sessionStorage
  private loadUserFromStorage() {
    try {
      const stored = sessionStorage.getItem('ga_current_user')
      if (stored) {
        this.currentUser = JSON.parse(stored)
        console.log('📥 Loaded user from storage:', this.currentUser?.username)
      }
    } catch (error) {
      console.warn('Failed to load user from storage:', error)
    }
  }

  // Save user to sessionStorage
  private saveUserToStorage() {
    try {
      if (this.currentUser) {
        sessionStorage.setItem('ga_current_user', JSON.stringify(this.currentUser))
        console.log('💾 Saved user to storage:', this.currentUser?.username)
      }
    } catch (error) {
      console.warn('Failed to save user to storage:', error)
    }
  }

  private isReady(): boolean {
    if (!this.isEnabled) return false
    if (typeof window.gtag === 'undefined') {
      console.warn('⚠️ Google Analytics not loaded yet')
      return false
    }
    return true
  }

  // USER IDENTIFICATION
  identifyUser(user: User) {
    if (!this.isReady()) return

    console.log('👤 Identifying user:', user.email)

    // Store user data
    this.currentUser = {
      email: user.email,
      username: user.username || user.email.split('@')[0],
      role: user.role,
      organization: user.organization || 'Unknown',
      plan: user.plan || 'Free'
    }

    // Persist to sessionStorage
    this.saveUserToStorage()

    window.gtag('config', this.measurementId, {
      user_id: user.id
    })

    window.gtag('set', 'user_properties', {
      user_email: user.email,
      user_username: this.currentUser.username,
      user_role: user.role,
      organization: user.organization || 'Unknown',
      account_created: user.createdAt || 'Unknown',
      plan: user.plan || 'Free'
    })

    console.log('✅ User identified and persisted:', this.currentUser.username)
  }

  clearUser() {
    this.currentUser = null
    sessionStorage.removeItem('ga_current_user')
    console.log('👋 User logged out and cleared from storage')
  }

  // PAGE TRACKING
  trackPageView(pagePath: string, pageTitle?: string) {
    if (!this.isReady()) return

    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle || pagePath,
      page_location: window.location.href,
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    })

    console.log('📄 Page view:', pagePath, 'User:', this.currentUser?.username)
  }

  // AUTHENTICATION
  trackLogin(method: string = 'email') {
    this.trackEvent('login', {
      method: method
    })
  }

  trackSignup(method: string = 'email', role?: string) {
    this.trackEvent('sign_up', {
      method: method,
      user_role: role
    })
  }

  trackLogout() {
    this.trackEvent('logout', {})
    this.clearUser()
  }

  trackPasswordReset() {
    this.trackEvent('password_reset', {})
  }

  trackEmailVerification() {
    this.trackEvent('email_verified', {})
  }

  // BRANCH SELECTION
  trackBranchSelected(branchId: number, branchName: string) {
    if (!this.isReady()) return

    window.gtag('event', 'branch_selected', {
      branch_id: branchId,
      branch_name: branchName,
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    })

    console.log('🎯 Branch selected:', branchId, branchName, 'User:', this.currentUser?.username)
  }

  // QUESTIONNAIRE TRACKING
  trackQuestionnaireStarted(branchId: number, branchName?: string) {
    if (!this.isReady()) return

    window.gtag('event', 'questionnaire_started', {
      branch_id: branchId,
      branch_name: branchName || `Branch ${branchId}`,
      timestamp: new Date().toISOString(),
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    })

    console.log('▶️ Questionnaire started:', branchId, branchName, 'User:', this.currentUser?.username)
  }

  trackQuestionAnswered(
    branchId: number,
    branchName: string,
    questionNumber: number,
    propertyId: number,
    propertyName: string,
    totalQuestions: number
  ) {
    if (!this.isReady()) return

    window.gtag('event', 'question_answered', {
      branch_id: branchId,
      branch_name: branchName,
      question_number: questionNumber,
      property_id: propertyId,
      property_name: propertyName,
      total_questions: totalQuestions,
      progress_percent: Math.round((questionNumber / totalQuestions) * 100),
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    })

    // console.log('📋 Question answered:', {
    //   branch: branchName,
    //   question: questionNumber,
    //   user: this.currentUser?.username
    // })
  }

  trackRecommendationsViewed(
    branchId: number,
    branchName: string,
    recommendationCount: number,
    dataCompleteness: string
  ) {
    if (!this.isReady()) return

    window.gtag('event', 'recommendations_viewed', {
      branch_id: branchId,
      branch_name: branchName,
      recommendation_count: recommendationCount,
      data_completeness: dataCompleteness,
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    })

    // console.log('📊 Recommendations viewed:', {
    //   branch: branchName,
    //   count: recommendationCount,
    //   user: this.currentUser?.username
    // })
  }

  trackQuestionnaireCompleted(
    branchId: number,
    branchName: string,
    totalQuestions: number,
    answeredQuestions: number,
    durationSeconds?: number
  ) {
    if (!this.isReady()) return

    window.gtag('event', 'questionnaire_completed', {
      branch_id: branchId,
      branch_name: branchName,
      total_questions: totalQuestions,
      answered_questions: answeredQuestions,
      completion_rate: Math.round((answeredQuestions / totalQuestions) * 100),
      duration_seconds: durationSeconds || 0,
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    })

    console.log('✅ Questionnaire completed:', {
      branch: branchName,
      user: this.currentUser?.username
    })
  }

  trackTreatmentHistoryAdded(branchId: number, treatmentName: string) {
    if (!this.isReady()) return

    window.gtag('event', 'treatment_history_added', {
      branch_id: branchId,
      treatment_name: treatmentName,
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role
    })

    console.log('💊 Treatment history added:', treatmentName)
  }

  // EXPERT FEATURES
  trackToolCreated(toolType?: string) {
    this.trackEvent('tool_created', {
      tool_type: toolType || 'unknown'
    })
  }

  trackToolEdited(toolId: string, toolType?: string) {
    this.trackEvent('tool_edited', {
      tool_id: toolId,
      tool_type: toolType || 'unknown'
    })
  }

  trackToolDeleted(toolId: string) {
    this.trackEvent('tool_deleted', {
      tool_id: toolId
    })
  }

  trackTnmEdit(toolId: string) {
    this.trackEvent('tnm_edited', {
      tool_id: toolId
    })
  }

  // VOTING & RATINGS
  trackVoteSubmitted(itemId: string, voteValue: number) {
    this.trackEvent('vote_submitted', {
      item_id: itemId,
      vote_value: voteValue
    })
  }

  trackRatingViewed() {
    this.trackEvent('ratings_viewed', {})
  }

  trackVotingDetailViewed(itemId: string) {
    this.trackEvent('voting_detail_viewed', {
      item_id: itemId
    })
  }

  // SELF-CHECK
  trackSelfCheckStarted() {
    this.trackEvent('selfcheck_started', {})
  }

  trackSelfCheckCompleted(durationSeconds?: number) {
    this.trackEvent('selfcheck_completed', {
      duration_seconds: durationSeconds || 0
    })
  }

  // REPORTS
  trackReportGenerated(reportType: string, userRole: string) {
    this.trackEvent('report_generated', {
      report_type: reportType,
      user_role: userRole
    })
  }

  trackReportExported(reportType: string, format: string) {
    this.trackEvent('report_exported', {
      report_type: reportType,
      export_format: format
    })
  }

  // ADMIN FEATURES
  trackDataLoad(dataType: string, recordCount?: number) {
    this.trackEvent('data_loaded', {
      data_type: dataType,
      record_count: recordCount || 0
    })
  }

  trackTnmConfiguration(action: string) {
    this.trackEvent('tnm_configuration', {
      configuration_action: action
    })
  }

  trackTestAnalysisViewed(testId: string) {
    this.trackEvent('test_analysis_viewed', {
      test_id: testId
    })
  }

  trackSystemSettingsChanged(settingName: string) {
    this.trackEvent('system_settings_changed', {
      setting_name: settingName
    })
  }

  trackAdminPanelAccessed() {
    this.trackEvent('admin_panel_accessed', {})
  }

  // ENGAGEMENT
  trackButtonClick(buttonName: string, location: string) {
    this.trackEvent('button_click', {
      button_name: buttonName,
      button_location: location
    })
  }

  trackSearch(searchQuery: string, resultCount?: number) {
    this.trackEvent('search', {
      search_term: searchQuery,
      result_count: resultCount || 0
    })
  }

  trackFormSubmit(formName: string) {
    this.trackEvent('form_submit', {
      form_name: formName
    })
  }

  // ERRORS
  trackError(errorMessage: string, errorType: string, errorLocation?: string) {
    this.trackEvent('app_error', {
      error_message: errorMessage,
      error_type: errorType,
      error_location: errorLocation || 'unknown'
    })
  }

  trackApiError(endpoint: string, statusCode: number, errorMessage?: string) {
    this.trackEvent('api_error', {
      api_endpoint: endpoint,
      status_code: statusCode,
      error_message: errorMessage || 'unknown'
    })
  }

  // GENERIC EVENT
  trackEvent(eventName: string, params?: Record<string, any>) {
    if (!this.isReady()) return

    const enrichedParams = {
      ...params,
      user_email: this.currentUser?.email,
      user_username: this.currentUser?.username,
      user_role: this.currentUser?.role,
      user_organization: this.currentUser?.organization
    }

    window.gtag('event', eventName, enrichedParams)
    console.log('🎯 Event:', eventName, 'User:', this.currentUser?.username)
  }
}

export const analytics = new AnalyticsService()

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}